import { Box, Grid, Typography, Button, TextField, Dialog } from '@mui/material';
import React, { useState, ChangeEvent, useEffect } from 'react';
import { useStyles } from '../../surveyStyles';
import { postSurvey } from '../../../../Utils/apiService';
import { enqueueSnackbar } from 'notistack';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '9px',
};

interface FormData {
  [key: string]: string | string[];
}

const BeginSurveyDynamic = (props: any) => {
  const { questions, handleClose, userData } = props;
  const questionsData = questions?.questions || [];
  const [formData, setFormData] = useState<FormData>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [openConfirm, setOpenConfirm] = React.useState(false);
  const classes = useStyles();

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = event.target;

    setFormData((prevState) => {
      if (type === 'checkbox') {
        const previousValues = (prevState[name] as string[]) || [];
        return {
          ...prevState,
          [name]: checked ? [...previousValues, value] : previousValues.filter((val) => val !== value),
        };
      } else if (type === 'radio') {
        return { ...prevState, [name]: value };
      } else {
        return { ...prevState, [name]: value };
      }
    });
  };

  const handleChangeShortAns = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [questionsData[currentIndex]?.id]: event.target.value,
    }));
  };

  const handleNext = () => {
    const currentQuestion = questionsData[currentIndex];
    let currentAnswer = formData[currentQuestion?.id];

    // Handle Linear Scale separately
    if (currentQuestion?.type === 'Linear Scale') {
      currentAnswer = selectedIndex ? selectedIndex.toString() : '';
      setFormData((prevState) => ({
        ...prevState,
        [currentQuestion.id]: currentAnswer,
      }));
    }

    // Validate required fields
    if (currentQuestion?.required) {
      const isEmptyAnswer =
        !currentAnswer ||
        (Array.isArray(currentAnswer) && currentAnswer.length === 0) ||
        (typeof currentAnswer === 'string' && currentAnswer.trim() === '');

      if (isEmptyAnswer) {
        enqueueSnackbar({
          message: 'Please answer the question before proceeding',
          variant: 'warning',
          autoHideDuration: 1500,
        });
        return;
      }
    }

    if (currentIndex < questionsData.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
      setSelectedIndex(null); // Reset selectedIndex for the next question
    }
  };

  const postSurveys = () => {
    const formattedAnswers = questionsData.map((question: any) => {
      const answer = formData[question.id] || '';
      return { parameterId: question.id, answer: Array.isArray(answer) ? answer.join(', ') : answer };
    });

    const payload = {
      contactId: userData.applicantId,
      surveyId: questions?.id,
      answers: formattedAnswers,
    };
    postSurvey(payload)
      .then((res) => {
        if (res?.data?.success) {
          handleClose();
          enqueueSnackbar({ message: 'Survey Saved Successfully', variant: 'success', autoHideDuration: 1000 });
        } else {
          enqueueSnackbar({ message: 'Something went wrong', variant: 'error', autoHideDuration: 1000 });
        }
      })
      .catch(() => {
        enqueueSnackbar({ message: 'Something went wrong', variant: 'error', autoHideDuration: 1000 });
      });
  };

  const currentQuestion = questionsData[currentIndex];
  const options = currentQuestion?.options || [];

  return (
    <Box sx={style}>
      <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>Survey Questions</Typography>
      <hr style={{ border: '1px solid #BFCEFF' }} />
      <Box sx={{ height: '50vh' }}>
        <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
          {`${currentIndex + 1}) ${currentQuestion?.question}`} {currentQuestion?.required && <span style={{ color: 'red' }}> *</span>}
        </Typography>

        {/* Yes/No Type */}
        {currentQuestion?.type === 'Yes/No' && (
          <>
            {options.map((option: string, index: number) => (
              <div key={index} style={{ marginTop: '10px' }}>
                <input
                  type="radio"
                  name={currentQuestion.id}
                  value={option}
                  checked={formData[currentQuestion.id] === option}
                  onChange={handleChange}
                />
                <label style={{ marginLeft: '10px', fontSize: '12px' }}>{option}</label>
              </div>
            ))}
          </>
        )}

        {/* Multiple Choice */}
        {currentQuestion?.type === 'Multiple Choice' && (
          <>
            {options.map((option: string, index: number) => (
              <div key={index} style={{ marginTop: '10px' }}>
                <input
                  type="checkbox"
                  name={currentQuestion.id}
                  value={option}
                  checked={formData[currentQuestion.id]?.includes(option) || false}
                  onChange={handleChange}
                />
                <label style={{ marginLeft: '10px', fontSize: '12px' }}>{option}</label>
              </div>
            ))}
          </>
        )}

        {/* Linear Scale */}
        {currentQuestion?.type === 'Linear Scale' && (
          <Grid container spacing={2} mt={2} justifyContent="center">
            {/* First Line - Numbers */}
            <Grid container item spacing={2} justifyContent="center">
              {[1, 2, 3, 4, 5].map((num) => (
                <Grid item key={num} xs={1.5} sx={{ textAlign: 'center' }} onClick={() => {
                  setSelectedIndex(num); // Update selectedIndex
                  setFormData((prevState) => ({
                    ...prevState,
                    [currentQuestion.id]: num.toString(), // Save to formData
                  }));
                }}>
                  <Box
                    sx={{
                      height: '36px',
                      width: '36px',
                      backgroundColor: formData[currentQuestion.id] === num.toString() ? '#2C58E8' : '#F6F8FB',
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: '12px',
                      color: formData[currentQuestion.id] === num.toString() ? 'white' : '#1A358C',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}

                  >
                    {num}
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Second Line - Min & Max Labels */}
            <Grid container item spacing={2} justifyContent="center">
              {[1, 2, 3, 4, 5].map((num, index, arr) => (
                <Grid item key={num} xs={1.5} sx={{ textAlign: 'center' }}>
                  {index === 0 && (
                    <Typography sx={{ fontSize: '12px', width: 50, color: '#1A358C', marginTop: '5px' }}>
                      {currentQuestion?.Min_Grade_Label__c}
                    </Typography>
                  )}
                  {index === arr.length - 1 && (
                    <Typography sx={{ fontSize: '12px', width: 50, color: '#1A358C', marginTop: '5px' }}>
                      {currentQuestion?.Max_Grade_Label__c}
                    </Typography>
                  )}
                </Grid>
              ))}
            </Grid>
          </Grid>

        )}

        {/* Short Answer */}
        {currentQuestion?.type === 'Short Answer' && (
          <TextField
            variant="outlined"
            placeholder="Enter"
            value={formData[currentQuestion?.id] || ''}
            onChange={handleChangeShortAns}
            fullWidth
          />
        )}
      </Box>

      <Box sx={{ float: 'right', marginTop: '20px', display: 'flex', gap: '10px' }}>
        <Button style={{ background: "#E8EBF3", color: "#373A45",width:"100px" }} onClick={currentIndex > 0 ? () => setCurrentIndex(currentIndex - 1) : handleClose}>Back</Button>
        <Button
          onClick={() => {
            if (currentIndex === questionsData.length - 1) {
              setOpenConfirm(true); // Open confirmation dialog
            } else {
              handleNext();
            }
          }}
          style={{ background: "var(--Gradient,linear-gradient(90deg,#F7A703,#FFE090)) !important", color: "#5B4200",width:"100px" }}
        >
          {currentIndex < questionsData.length - 1 ? 'Next' : 'Submit'}
        </Button>
      </Box>

      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <Box p={3} minWidth={320} textAlign="center">
          <Typography fontSize={14} fontWeight={500} mb={3}>
            Are you sure you want to submit your survey?
          </Typography>
          <Box display="flex" justifyContent="center" gap={2} mt={2}>
            <Button
              variant="text"
              onClick={() => setOpenConfirm(false)}
              sx={{ minWidth: 100 }}
            >
              No
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setOpenConfirm(false);
                postSurveys();
              }}
              sx={{
                minWidth: 100,
                background: 'linear-gradient(90deg, #FAB400 0%, #FFE090 100%)',
                color: '#222',
                fontWeight: 500,
                boxShadow: 'none',
                // '&:hover': { background: 'linear-gradient(90deg, #FFD600 0%, #FFB800 100%)' }
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default BeginSurveyDynamic;